import { assertOriginalRuleConfiguration } from '../utils/originalRuleConfiguration';
/**
 * AccountabilityRuleService - Handles accountability period rule processing
 *
 * Accountability rules create temporary periods where any offense triggers
 * an immediate consequence, and each additional offense extends the period.
 */

import { isSchoolRulePeriodError } from '../utils/dateHelpers';
import { isPhaseExecutionError, phaseRuleAllowsRecord } from '../../domain/phaseRuntime';
import { matchesAccountabilityOffence } from '../utils/accountabilityEligibility';

import {
  collection,
  query,
  where,
  getDocs,
  getDocsFromServer,
  getDocFromServer,
  addDoc,
  doc,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { db, functions, httpsCallable } from '../../../firebase';
import { getOffenceField } from '../utils/fieldNormalizer';
import { getDateRangeForTimeframe } from '../utils/dateHelpers';
import { getDateRangeForRuleWithDetection } from '../utils/ruleDateHelpers';
import { DISCIPLINE_TYPES } from '../constants';
import { disciplineLogger } from '../../../utils/disciplineLogger';
import { processingGuard } from '../utils/ProcessingGuard';
import { snapshotStaffIdentity } from '../utils/staffIdentity';

export class AccountabilityRuleService {
  constructor(dependencies) {
    this.ruleService = dependencies.ruleService;
    this.usesTrustedAccountability = !this.ruleService?.collectionName
      || this.ruleService.collectionName === 'disciplineRules';
    this.mentionService = dependencies.mentionService;
    this.consequenceService = dependencies.consequenceService;
  }

  /**
   * Process accountability rules for a mention
   * @param {Object} mention - The mention to process
   * @param {Object} studentData - Pre-fetched student data
   * @returns {Promise<boolean>} True if a rule was triggered
   */
  async processAccountabilityRules(mention, studentData) {
    try {
      disciplineLogger.info(`Processing accountability rules for: ${getOffenceField(mention)}`, null, 'accountability');

      if (this.usesTrustedAccountability) {
        const saved = await this.recoverSavedResult(mention);
        if (saved) {
          await this.consequenceService.resumeAccountabilityConsequence(saved);
          return true;
        }
      }

      // Get all active accountability rules
      const activeRules = await this.ruleService.getActiveRules(mention.schoolId);
      const phasePolicy = await this.ruleService.getPhasePolicy?.(mention.schoolId);
      assertOriginalRuleConfiguration(mention.ruleConfiguration, activeRules, phasePolicy);
      const accountabilityRules = activeRules.filter(r => r.type === 'accountability');

      if (accountabilityRules.length === 0) {
        disciplineLogger.debug('No active accountability rules found', null, 'accountability');
        return false;
      }

      disciplineLogger.info(`Evaluating ${accountabilityRules.length} accountability rules`, null, 'accountability');

      let triggered = false;
      for (const rule of accountabilityRules) {
        const ruleTriggered = await this.evaluateRule(rule, mention, studentData, phasePolicy);
        if (ruleTriggered) {
          triggered = true;
        }
      }

      return triggered;
    } catch (error) {
      if (error.ruleConfigurationChanged || isSchoolRulePeriodError(error) || isPhaseExecutionError(error)) throw error;
      console.error('Error processing accountability rules:', error);
      throw error;
    }
  }

  async recoverSavedResult(mention) {
    const {data} = await httpsCallable(functions, 'readBehaviourAccountabilityResult')({
      schoolId: mention.schoolId, studentId: mention.studentId, mentionId: mention.id,
    });
    if (!data.actionId) return null;
    const snapshot = await getDocFromServer(doc(db, 'schools', mention.schoolId, 'disciplineQueue', data.actionId));
    if (!snapshot.exists()) throw new Error('The saved accountability response could not be read. Retry the original Mention.');
    return {...snapshot.data(), id: snapshot.id};
  }

  /**
   * Evaluate a single accountability rule
   * @param {Object} rule - The rule to evaluate
   * @param {Object} mention - The mention to check
   * @param {Object} studentData - Pre-fetched student data
   * @returns {Promise<boolean>} True if rule was triggered
   */
  async evaluateRule(rule, mention, studentData, phasePolicy) {
    phasePolicy ??= await this.ruleService.getPhasePolicy?.(mention.schoolId);
    if (!phaseRuleAllowsRecord(phasePolicy, rule, mention)) return false;
    // Check if student is currently in an accountability period for this rule
    const currentPeriod = await this.getActiveAccountabilityPeriod(
      mention.studentId,
      rule.id,
      mention.schoolId,
      studentData
    );

    if (currentPeriod) {
      // Student is in accountability - apply consequence and extend period
      return await this.handleDuringAccountability(rule, mention, currentPeriod, phasePolicy);
    } else {
      // Check if should trigger new accountability period
      return await this.checkTriggerConditions(rule, mention, studentData, phasePolicy);
    }
  }

  /**
   * Handle offense during an active accountability period
   * @param {Object} rule - The accountability rule
   * @param {Object} mention - The triggering mention
   * @param {Object} currentPeriod - The active accountability period
   * @returns {Promise<void>}
   */
  async handleDuringAccountability(rule, mention, currentPeriod, phasePolicy) {
    phasePolicy ??= await this.ruleService.getPhasePolicy?.(mention.schoolId);
    if (!phaseRuleAllowsRecord(phasePolicy, rule, mention)) return false;
    disciplineLogger.critical(`Student in accountability period for rule: ${rule.title}`, null, 'accountability');

    const offenseName = getOffenceField(mention);

    if (!matchesAccountabilityOffence(mention, rule, currentPeriod)) return false;

    const consequenceType = rule.duringConsequence || 'detention';
    const studentFirstName = mention.studentName?.split(' ')[0] || 'Student';

    // Calculate which day of accountability period
    const startDate = currentPeriod.startDate?.toDate?.() || new Date(currentPeriod.startDate);
    const now = new Date();
    const daysPassed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24)) + 1;
    const totalDays = currentPeriod.totalDays || rule.periodLength || 7;

    const consequenceDetails = `${studentFirstName} received ${consequenceType} during accountability period.`;

    const queueItem = {
      schoolId: mention.schoolId,
      studentId: mention.studentId,
      studentName: mention.studentName,
      className: mention.className,
      classId: mention.classId,
      type: consequenceType,
      status: 'pending',
      requiresAction: true,
      priority: 'high',
      severity: 3,
      triggeredByRule: rule.id,
      ruleName: rule.title,
      ruleType: 'accountability',
      triggeredBy: [mention.id],
      triggeringMentions: [mention.id],
      accountabilityPeriodId: currentPeriod.id,
      accountabilityDay: daysPassed,
      accountabilityTotalDays: totalDays,
      consequenceDetails: consequenceDetails,
      notes: `During accountability period - any offense triggers ${consequenceType}`,
      ...snapshotStaffIdentity(mention),
      createdBy: 'system-accountability',
      autoGenerated: true,
      offence: offenseName
    };

    // Create consequence
    const result = await this.consequenceService.createDisciplineConsequence(queueItem, {
      phaseSource: { collection: 'mentions', id: mention.id },
      ...(this.usesTrustedAccountability ? { accountability: { operation: 'extend', ruleId: rule.id,
        expectedRuleRevision: rule.configurationRevision ?? 0, periodId: currentPeriod.id } } : {}),
    });
    disciplineLogger.critical('Accountability consequence created', { id: result.id, type: consequenceType }, 'accountability');

    if (!this.usesTrustedAccountability) {
      await this.mentionService.updateMention(mention.id, {
        schoolId: mention.schoolId,
        triggeredConsequences: [...(mention.triggeredConsequences || []), result.id],
        triggeredAccountability: true, actioned: true, actionedBy: [rule.id], actionedAt: Timestamp.now(),
      });
      await this.extendLegacyAccountabilityPeriod(currentPeriod, rule);
    }
    return true;
  }

  /**
   * Check if conditions are met to trigger a new accountability period
   * @param {Object} rule - The accountability rule
   * @param {Object} mention - The triggering mention
   * @param {Object} studentData - Pre-fetched student data
   * @returns {Promise<boolean>} True if period was created
   */
  async checkTriggerConditions(rule, mention, studentData = null, phasePolicy) {
    phasePolicy ??= await this.ruleService.getPhasePolicy?.(mention.schoolId);
    if (!phaseRuleAllowsRecord(phasePolicy, rule, mention)) return false;
    // Check if mention's offense matches trigger offenses
    const triggerOffenses = rule.triggerOffenses || [];
    if (!matchesAccountabilityOffence(mention, rule)) {
      return false;
    }

    // OPTIMIZATION: Use pre-fetched data if available
    const timeframe = rule.triggerTimeframe || 'month';
    let previousMentions;

    if (studentData && studentData.mentions) {
      // Use pre-fetched mentions (much faster, no query needed!)
      const dateRange = await getDateRangeForRuleWithDetection(rule, timeframe, 1, mention.schoolId || studentData.schoolId, {
        referenceDate: mention.issuedAt || mention.createdAt,
      });

      const offensesToCheck = triggerOffenses.length > 0 ? triggerOffenses : [getOffenceField(mention)];

      previousMentions = studentData.mentions.filter(m => {
        // Check date range
        const recordedTime = m.issuedAt || m.createdAt;
        const mentionDate = recordedTime?.toDate?.() || new Date(recordedTime);
        if (Number.isNaN(mentionDate.getTime())) return false;
        if (mentionDate < dateRange.start || mentionDate > dateRange.end) return false;

        // Check if not actioned
        if (m.actioned) return false;

        // Check offense match
        return matchesAccountabilityOffence(m, {triggerOffenses: offensesToCheck});
      });

      disciplineLogger.info(`Using pre-fetched mentions for accountability check (no query!)`, {
        totalMentions: studentData.mentions.length,
        matchingMentions: previousMentions.length
      }, 'accountability');
    } else {
      // Fallback to query if no pre-fetched data
      previousMentions = await this.getPreviousMentionsForOffenses(
        mention.studentId,
        triggerOffenses.length > 0 ? triggerOffenses : [getOffenceField(mention)],
        timeframe,
        mention.schoolId, // Pass schoolId for hierarchical query
        mention.issuedAt || mention.createdAt
      );
    }

    previousMentions = previousMentions.filter(candidate => candidate.id !== mention.id
      && phaseRuleAllowsRecord(phasePolicy, rule, candidate, { historical: true }));
    const totalCount = previousMentions.length + 1;
    disciplineLogger.info(`Student has ${totalCount} offenses in ${timeframe}`, null, 'accountability');

    // Check if trigger threshold is met
    if (totalCount >= (rule.triggerCount || 3)) {
      disciplineLogger.critical(`Accountability rule triggered: ${rule.title}`, { totalCount, threshold: rule.triggerCount || 3 }, 'accountability');
      await this.createAccountabilityPeriod(mention, rule, previousMentions);
      return true;
    }

    return false;
  }

  /**
   * Get active accountability period for a student and rule
   * @param {string} studentId - Student ID
   * @param {string} ruleId - Rule ID
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @param {Object} studentData - Pre-fetched student data (optional)
   * @returns {Promise<Object|null>} Active period or null
   */
  async getActiveAccountabilityPeriod(studentId, ruleId, schoolId, studentData = null) {
    try {
      const now = new Date();

      if (studentData && studentData.accountabilityPeriods) {
        // Use pre-fetched data
        const activePeriod = studentData.accountabilityPeriods.find(p =>
          p.studentId === studentId &&
          p.ruleId === ruleId &&
          (p.endDate?.toDate?.() || new Date(p.endDate)) > now &&
          p.active === true
        );
        if (activePeriod) return activePeriod;
      }

      if (!schoolId) {
        console.error('Cannot query accountability periods: schoolId is missing');
        return null;
      }

      // Query hierarchical path
      const periodsQuery = query(
        collection(db, 'schools', schoolId, 'accountabilityPeriods'),
        where('studentId', '==', studentId),
        where('ruleId', '==', ruleId),
        where('endDate', '>', Timestamp.fromDate(now)),
        where('active', '==', true)
      );

      const snapshot = await getDocsFromServer(periodsQuery);
      processingGuard.trackReads('AccountabilityService.getActivePeriod', snapshot.size, {
        studentId,
        ruleId
      });
      if (snapshot.empty) return null;

      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    } catch (error) {
      if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error)) throw error;
      console.error('Error getting active accountability period:', error);
      throw error;
    }
  }

  /**
   * Create a new accountability period
   * @param {Object} mention - The triggering mention
   * @param {Object} rule - The accountability rule
   * @param {Array} previousMentions - Previous mentions that triggered this
   * @returns {Promise<void>}
   */
  async createAccountabilityPeriod(mention, rule, previousMentions) {
    try {
      const schoolId = mention.schoolId;
      if (!schoolId) {
        throw new Error('Cannot create accountability period: mention.schoolId is missing');
      }

      const legacyPeriod = this.usesTrustedAccountability ? null
        : await this.createLegacyAccountabilityPeriod(mention, rule, previousMentions);
      const studentFirstName = mention.studentName?.split(' ')[0] || 'Student';

      // Get the triggering offense from the most recent mention
      const triggeringOffense = getOffenceField(mention);

      // Create accountability consequence card
      const queueItem = {
        schoolId: mention.schoolId,
        studentId: mention.studentId,
        studentName: mention.studentName,
        className: mention.className,
        classId: mention.classId,
        type: DISCIPLINE_TYPES.ACCOUNTABILITY,  // Create as accountability type consequence
        status: 'pending',  // Show in queue, requires acknowledgment
        requiresAction: true,
        priority: 'high',
        severity: 3,  // High severity to stand out
        triggeredByRule: rule.id,
        ruleName: rule.title,
        ruleType: 'accountability-entry',
        triggeredBy: [mention.id, ...previousMentions.map(m => m.id)],
        triggeringMentions: [
          mention.id,
          ...previousMentions.map(m => m.id),
        ],
        ...(legacyPeriod ? { accountabilityPeriodId: legacyPeriod.id } : {}),
        accountabilityPeriodDays: rule.periodLength || 7,
        consequenceDetails: `${studentFirstName} entered ${rule.periodLength || 7}-day accountability period for ${triggeringOffense}`,
        notes: `${rule.triggerCount} ${triggeringOffense} offenses in ${rule.triggerTimeframe} triggered accountability. During this period: repeat offense → ${rule.duringConsequence}`,
        ...snapshotStaffIdentity(mention),
        createdBy: 'system-accountability',
        autoGenerated: true,
        offence: triggeringOffense
      };

      // Create accountability consequence (don't trigger thresholds for accountability entry)
      await this.consequenceService.createDisciplineConsequence(queueItem, {
        skipThresholds: true, phaseSource: { collection: 'mentions', id: mention.id },
        ...(this.usesTrustedAccountability ? { accountability: { operation: 'start', ruleId: rule.id,
          expectedRuleRevision: rule.configurationRevision ?? 0 } } : {}),
      });
      if (legacyPeriod) {
        for (const source of [mention, ...previousMentions]) {
          await this.mentionService.updateMention(source.id, {
            schoolId: source.schoolId, triggeredAccountabilityPeriod: true, actioned: true,
            actionedBy: [rule.id], actionedAt: Timestamp.now(),
          });
        }
      }
    } catch (error) {
      if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error)) throw error;
      console.error('Error creating accountability period:', error);
      throw error;
    }
  }

  // Recognition retains its existing adapter until its trusted recording path
  // is implemented; Behaviour-only persistence must not consume its events.
  async createLegacyAccountabilityPeriod(mention, rule, previousMentions) {
    const schoolId = mention.schoolId;
    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + (rule.periodLength || 7));

    const period = {
      schoolId, // Add schoolId to document
      studentId: mention.studentId,
      studentName: mention.studentName,
      ruleId: rule.id,
      ruleName: rule.title,
      startDate: Timestamp.fromDate(startDate),
      endDate: Timestamp.fromDate(endDate),
      originalEndDate: Timestamp.fromDate(endDate),
      extensionCount: 0,
      totalDays: rule.periodLength || 7,
      triggeringOffenses: [mention.id, ...previousMentions.map(m => m.id)],
      triggeringOffenseText: getOffenceField(mention), // Store the actual offense text
      violationCount: 0,
      active: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Use hierarchical path
    return addDoc(collection(db, 'schools', schoolId, 'accountabilityPeriods'), period);
  }

  async extendLegacyAccountabilityPeriod(period, rule) {
    try {
      const currentEndDate = period.endDate?.toDate?.() || new Date(period.endDate);
      const newEndDate = new Date(currentEndDate);
      const extensionDays = rule.extensionLength || 7;
      const maxDays = rule.maxTotalPeriod || 21;

      newEndDate.setDate(newEndDate.getDate() + extensionDays);

      // Check if we've hit the maximum period
      const totalDays = period.totalDays + extensionDays;
      if (totalDays > maxDays) {
        disciplineLogger.info('Accountability period at maximum length', { totalDays, maxDays }, 'accountability');
        return;
      }

      // CRITICAL: Use hierarchical path for accountabilityPeriods
      // The period object should have schoolId from when it was created
      const schoolId = period.schoolId;
      if (!schoolId) {
        disciplineLogger.error('Cannot extend accountability period: schoolId is missing', {
          periodId: period.id,
        });
        return;
      }

      await updateDoc(doc(db, 'schools', schoolId, 'accountabilityPeriods', period.id), {
        endDate: Timestamp.fromDate(newEndDate),
        extensionCount: (period.extensionCount || 0) + 1,
        totalDays: totalDays,
        violationCount: (period.violationCount || 0) + 1,
        updatedAt: Timestamp.now()
      });

      disciplineLogger.critical(`Accountability period extended by ${extensionDays} days`, { totalDays }, 'accountability');
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error extending accountability period:', error);
    }
  }

  /**
   * Get previous mentions for specific offenses within a timeframe
   * @param {string} studentId - Student ID
   * @param {Array} offenses - Offense names to search for
   * @param {string} timeframe - Timeframe to search
   * @returns {Promise<Array>} Previous mentions
   */
  async getPreviousMentionsForOffenses(studentId, offenses, timeframe, schoolId, referenceDate = new Date()) {
    try {
      const dateRange = await getDateRangeForTimeframe(timeframe, 1, schoolId, referenceDate);

      // CRITICAL: Validate schoolId for hierarchical query
      if (!schoolId) {
        disciplineLogger.critical('❌ Cannot query mentions: schoolId is missing', {
          studentId,
          offensesCount: offenses.length
        });
        return []; // Return empty array to avoid crash
      }

      // CRITICAL OPTIMIZATION: Single query instead of N queries for N offense types
      // This was causing massive read explosion (10 offenses = 10 queries per student!)
      const q = query(
        collection(db, 'schools', schoolId, 'mentions'),
        where('studentId', '==', studentId),
        where('actioned', '==', false),
        where('issuedAt', '>=', Timestamp.fromDate(dateRange.start))
      );

      const querySnapshot = await getDocs(q);

      disciplineLogger.info(`OPTIMIZATION: Single query for accountability (was ${offenses.length} queries)`, {
        studentId,
        timeframe,
        offensesToCheck: offenses.length,
        mentionsFetched: querySnapshot.size
      }, 'accountability');

      // Filter client-side for matching offenses (much cheaper than N queries)
      const matchingMentions = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(m => {
          const recordedDate = m.issuedAt?.toDate?.() || new Date(m.issuedAt || m.createdAt);
          if (Number.isNaN(recordedDate.getTime()) || recordedDate < dateRange.start || recordedDate > dateRange.end) return false;
          return matchesAccountabilityOffence(m, {triggerOffenses: offenses});
        });

      return matchingMentions;
    } catch (error) {
      if (isSchoolRulePeriodError(error) || isPhaseExecutionError(error)) throw error;
      console.error('Error getting previous mentions for offenses:', error);
      throw error;
    }
  }
}
